import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import lang from '../../lib/language';
import { ChangeIndividualUserNameRequest } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { changeIndividualUserName, getIndividualUserName } from '../../lib/store/crm';
import { CRMCall } from '../../lib/store/crm/types';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';
import { NullableString } from '../../lib/util/types';

import { useIsMobile } from '../../hooks';
import { ButtonTypesEnum } from '../Button/Button.types';
import { Button } from '..';

import styles from './ChangeUsernameModal.module.scss';

type Props = ReduxProps &{
    username: NullableString;
    onClose: () => void;
}

const ChangeUsernameModal = (props: Props) => {
  const {
    username,
    onClose,
    getUserName,
    changeUserName,
    individualId,
    isChangeUserNameCallReady,
    isUserNameUsed,
  } = props;

  const isMobile = useIsMobile();
  const usernameMinChars = 4;
  const usernameMaxChars = 16;

  const [profile_name, setProfileName] = useState('');
  const [usernameErrorText, setusernameErrorText] = useState('');
  const [showError, setShowError] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasMadeRequest, setHasMadeRequest] = useState(false);

  useEffect(() => {
    if (isUserNameUsed) {
      setusernameErrorText(lang.userNameExist());
      setShowError(true);
    } else if (isChangeUserNameCallReady && hasMadeRequest) {
      setHasMadeRequest(false);
      getUserName();
      onClose();
    }
  }, [isChangeUserNameCallReady, isUserNameUsed, hasMadeRequest, onClose, getUserName]);

  const validateUsername = () => {
    if (profile_name.length < usernameMinChars) {
      setusernameErrorText(lang.validationCapitalMinimumSymbols(usernameMinChars));
      setShowError(true);
    } else if (profile_name.length > usernameMaxChars) {
      setusernameErrorText(lang.validationCapitalMaximumSymbols(usernameMaxChars));
      setShowError(true);
    }
  };

  const handleSubmit = () => {
    validateUsername();
    if (!showError) {
      setHasMadeRequest(true);
      changeUserName({ individual_id: individualId as number, profile_name });
    }
  };

  const onInputFocus = () => {
    setHasMadeRequest(false);
    setIsInputFocused(true);
    setShowError(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileName(e.target.value);
    setShowError(false);
    setusernameErrorText('');
  };

  return (
    <div className={styles.wrapper}>
      {!isMobile && <div className={styles.title}>{lang.userNameEdit()}</div>}
      <div className={styles.inputWrapper}>
        <div className={styles.inputTitle}>{lang.userNameTitle()}</div>
        <input
          type="text"
          onFocus={onInputFocus}
          onBlur={() => setIsInputFocused(false)}
          className={classNames(styles.input, showError && styles.inputFail)}
          placeholder={isInputFocused ? '' : username?.toString()}
          value={profile_name}
          onChange={onInputChange}
        />
        {showError && <div className={styles.inputError}>{usernameErrorText}</div>}
      </div>
      <div className={styles.buttons}>
        <Button
          className={styles.buttonConfirm}
          fullWidth
          onClick={handleSubmit}
        >
          {lang.commonEditNamesMultiple()}
        </Button>
        <Button
          variant={ButtonTypesEnum.Tertiary}
          fullWidth
          onClick={onClose}
        >
          {lang.commonCancelMultiple()}
        </Button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  changeUserName: (data: ChangeIndividualUserNameRequest) => {
    dispatch(changeIndividualUserName(data));
  },
  getUserName: (): void => dispatch(getIndividualUserName()),
});

const mapStateToProps = (state: RootState) => ({
  isChangeUserNameCallReady: isCallStatusReady(CRMCall.changeIndividualUserName, state.crm.statusByCall),
  isUserNameUsed: state.crm.statusByCall[CRMCall.changeIndividualUserName]?.errorCode === 409,
  individualId: state.crm.individualId,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
  type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ChangeUsernameModal);
